import UserApi from "@/api/users/users";
import { User } from "@/types/Users";
import {
  DropDownItem,
  DropDownMenu,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import type { ButtonProps } from "@monolith-forensics/monolith-ui";
import type { WithTestId } from "@/types/testid";
import { useQuery } from "@tanstack/react-query";
import { useState, useRef } from "react";
import { Check } from "lucide-react";
import { usePermissions } from "@/hooks";
import { BasicUser } from "@/types/Users/Users";
import { UserAvatar } from "@/Features";

const UserLabel = ({ user }: { user: User }) => {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <UserAvatar text={user.full_name} id={user.user_id} />{" "}
      <span>{user.full_name}</span>
    </div>
  );
};

export const EvidenceAssignedUserBtnSelector: React.FC<{
  defaultValue?: string | number;
  value?: string | number;
  onChange?: (value: string | number, data: User) => void;
  query?: { user_id: number[] };
}> = ({ defaultValue, value, onChange, query }) => {
  const theme = useTheme();
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const isDisabled = !hasPermission(MONOLITH_PERMISSIONS.EVIDENCE_UPDATE);
  const isControlled = useRef(value !== undefined);
  const [stateValue, setStateValue] = useState<string | number | undefined>(
    defaultValue
  );
  const _value = isControlled.current ? value : stateValue;

  const { data } = useQuery<User[]>({
    queryKey: ["users", "list"],
    queryFn: () => UserApi.getUsers(),
    staleTime: 5000,
  });

  const handleChange = (value: DropDownItem) => {
    if (value.value === "") {
      setStateValue(undefined);
      onChange?.("", value.data);
      return;
    }
    setStateValue(value.value);
    onChange?.(value.value, value.data);
  };

  const UserTooltip = ({ user }: { user: User }) => (
    <div style={{ padding: "8px", fontSize: "12px" }}>
      <div>
        <strong>Name:</strong> {user.full_name}
      </div>
      <div>
        <strong>Title:</strong> {user.title || "N/A"}
      </div>
      <div>
        <strong>Email:</strong> {user.email}
      </div>
    </div>
  );

  const dropdownOptions: DropDownItem[] = [
    {
      value: "",
      label: "Unassigned",
      data: null as unknown as BasicUser,
      visible: true,
      rightSection: _value === "" || !_value ? <Check size={14} /> : null,
    },
    ...(data
      ?.sort((a, b) => a.full_name.localeCompare(b.full_name))
      ?.map((user) => {
        const userIdString = user?.user_id.toString();
        const isSelected = _value === userIdString;

        return {
          value: userIdString,
          label: user.full_name,
          data: {
            user_id: user.user_id,
            first_name: user.first_name,
            last_name: user.last_name,
            full_name: user.full_name,
            email: user.email,
          },
          visible: query?.user_id
            ? query.user_id.includes(user.user_id) && user.active === 1
            : user.active === 1,
          rightSection: isSelected ? <Check size={14} /> : null,
          leftSection: <UserAvatar text={user.full_name} id={user.user_id} />,
        };
      }) ?? []),
  ];

  const selectedUser = data?.find(
    (option) => option.user_id === Number(_value)
  );

  return (
    <DropDownMenu
      data={dropdownOptions}
      disabled={isDisabled}
      variant="contained"
      size="xs"
      onItemSelect={handleChange}
      TooltipContent={({ data: item }: { data: DropDownItem }) => {
        if (item?.data) {
          return <UserTooltip user={item.data} />;
        }
        if (item?.value === "") {
          return <>Remove assigned user from item</>;
        }
        return null;
      }}
      buttonProps={
        {
          size: "xs",
          "data-testid": "evidence-assign-button",
          style: {
            backgroundColor: theme.name === "DARK" ? "#303030" : "#f0f0f0",
            color:
              theme.name === "DARK" ? theme.palette.text.primary : "#000000",
            opacity: selectedUser ? 1 : 0.75,
            fontWeight: "normal",
            padding: "2px 5px",
            height: "24px",
            textDecoration: "none",
          },
        } as WithTestId<ButtonProps>
      }
      dropDownProps={{
        style: {
          width: 160,
        },
      }}
    >
      {selectedUser ? <UserLabel user={selectedUser} /> : "Unassigned"}
    </DropDownMenu>
  );
};
